import { DownwardOutlineIcon, UpwardOutlineIcon } from '@carboncopyco/icons';
import {
  ColorHex,
  TextCaption,
  TextLargeParagraph,
  TextPageHeader,
  TooltipBubble,
} from '@carboncopyco/ui';
import type { SeriesOptionsType } from 'highcharts';
import { type ReactNode, isValidElement } from 'react';
import { LineChart, getChartOptions } from '../../LineChart';
import { KpiLineChartSkeleton } from './KpiLineChartSkeleton';

export interface AnalyticsKpiLineChartProps {
  title: string;
  description: string | ReactNode;
  kpiNumber: number | string;
  kpiRawNumber?: number | string;
  kpiPercentage?: number | string;
  negative?: boolean;
  units?: string;
  lineChartData?: {
    values: number[];
    timestamps: string[];
  };
  loading?: boolean;
  color?: SeriesOptionsType['color'];
}

const skeletonClasses = 'bg-ds-natural-150 rounded-sm h-2 animate-pulse';

export const KpiLineChart = ({
  title = '',
  description,
  kpiNumber,
  kpiPercentage,
  negative,
  units,
  lineChartData,
  kpiRawNumber,
  loading,
  color,
}: AnalyticsKpiLineChartProps) => {
  return (
    <div className="w-full flex-col justify-between bg-ds-accent3-100 p-6 gap-4 rounded-lg">
      <div className="flex justify-between">
        <div className="flex flex-col w-1/2 pr-5">
          <div className="whitespace-nowrap mb-1.5">
            <TextLargeParagraph
              label={title}
              color="text-ds-natural-600"
            />
          </div>
          {loading ? (
            <div className={`${skeletonClasses} mt-4 w-10`} />
          ) : (
            <div className="flex items-end pt-3">
              <TooltipBubble
                placement="top"
                title={String(kpiRawNumber)}
              >
                <TextPageHeader
                  label={String(kpiNumber)}
                  color="text-ds-natural-600"
                />
              </TooltipBubble>
              {units && (
                <div className="mb-0.5 ml-1.5">
                  <TextCaption
                    label={units}
                    color="text-ds-natural-300"
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="w-1/2">
          {loading ? (
            <KpiLineChartSkeleton />
          ) : (
            lineChartData && (
              <LineChart
                options={getChartOptions({
                  data: lineChartData.values,
                  xCategories: lineChartData.timestamps,
                  name: title,
                  color,
                })}
              />
            )
          )}
        </div>
      </div>
      {loading ? (
        <div className={`${skeletonClasses} w-24 mt-7 flex items-center`} />
      ) : (
        <div className="flex items-center mt-4 gap-1">
          {!!kpiPercentage && (
            <div className="flex items-center gap-1">
              {negative ? (
                <DownwardOutlineIcon
                  size={12}
                  color={ColorHex.danger300}
                />
              ) : (
                <UpwardOutlineIcon
                  size={12}
                  color={ColorHex.accent2400}
                />
              )}
              <TextCaption
                label={`${String(kpiPercentage)}%`}
                color={negative ? 'text-ds-danger-300' : 'text-ds-accent2-400'}
              />
            </div>
          )}
          <div className="whitespace-nowrap flex items-center">
            {isValidElement(description) ? (
              description
            ) : (
              <TextCaption
                label={description}
                color="text-ds-natural-300"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
